import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import TeamCard from '../components/TeamCard';
import { teamMembers, stats } from '../utils/constants';

const About = () => {
    return (
        <>
            <Helmet>
                <title>About Us | LAMSAT Engineering Services</title>
                <meta
                    name="description"
                    content="Learn about LAMSAT Omer & Mohammed Technical Services LLC - A leading engineering service provider in Dubai with 30+ years of experience."
                />
                <meta property="og:title" content="About LAMSAT Engineering Services" />
                <meta property="og:description" content="Leading provider of electrical, plumbing, waterproofing, and civil construction services in UAE since 2016." />
            </Helmet>

            {/* Hero Section */}
            <section className="about-hero py-5 bg-primary-blue" style={{ marginTop: '80px' }}>
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center text-white"
                    >
                        <h1 className="display-5 fw-bold mb-3">About LAMSAT</h1>
                        <p className="lead mb-0 text-off-white" style={{ fontSize: '1.1rem' }}>
                            Excellence in Engineering Since 2016
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Company Overview */}
            <section className="py-5">
                <div className="container">
                    <div className="row align-items-center">
                        <motion.div
                            className="col-lg-6 mb-5 mb-lg-0"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="section-title mb-4">Our Story</h2>
                            <p className="lead mb-4">
                                Lamsat Omar And Mohammed Technical Services LLC is a leading provider of
                                electrical, plumbing, waterproofing, and civil construction services,
                                specializing in large-scale projects across the UAE.
                            </p>
                            <p className="mb-4">
                                Founded in 2016 by Mr. Mohd Sohag, LAMSAT has grown exponentially
                                from a small startup to a trusted name in engineering services.
                                With extensive experience in handling 72+ building-scale projects,
                                we are committed to delivering high-quality solutions that ensure
                                durability, safety, and efficiency.
                            </p>
                            <p className="mb-4">
                                Our journey is marked by a relentless pursuit of excellence,
                                technical innovation, and unwavering commitment to client satisfaction.
                                We believe in building relationships that last, just like the
                                structures we help create.
                            </p>
                            <div className="row mt-5">
                                <div className="col-6">
                                    <div className="text-center p-3">
                                        <div className="stat-number mb-2">2016</div>
                                        <h6 className="mb-0">Founded</h6>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="text-center p-3">
                                        <div className="stat-number mb-2">72+</div>
                                        <h6 className="mb-0">Projects</h6>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <img
                                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                                alt="LAMSAT Engineering Team"
                                className="img-fluid rounded-3 shadow-lg"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision - Glass-like Design */}
            <section className="mission-vision-section py-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title d-inline-block">Our Mission & Vision</h2>
                        <p className="text-cool-grey mt-3 mb-0" style={{ display: 'block' }}>
                            Driving excellence in every project we undertake
                        </p>
                    </motion.div>

                    <div className="row g-4">
                        {/* Mission Card - Glass-like with Red Theme */}
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <div className="glass-card h-100" style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                background: 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.8)',
                                boxShadow: '0 8px 32px rgba(199, 53, 63, 0.1)',
                                position: 'relative'
                            }}>
                                {/* Red gradient border */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, var(--primary-red), var(--light-red))'
                                }}></div>
                                
                                <div className="card-body p-4 p-md-5">
                                    {/* Enhanced Mission Icon with Glass Effect */}
                                    <div className="text-center mb-4">
                                        <div style={{
                                            width: '90px',
                                            height: '90px',
                                            background: 'linear-gradient(135deg, rgba(199, 53, 63, 0.2), rgba(229, 83, 93, 0.1))',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 20px',
                                            position: 'relative',
                                            backdropFilter: 'blur(5px)',
                                            border: '1px solid rgba(199, 53, 63, 0.3)',
                                            boxShadow: '0 8px 32px rgba(199, 53, 63, 0.15)'
                                        }}>
                                            <div style={{
                                                width: '70px',
                                                height: '70px',
                                                background: 'linear-gradient(135deg, var(--primary-red), var(--light-red))',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 15px rgba(199, 53, 63, 0.3)',
                                                animation: 'pulse 2s ease-in-out infinite'
                                            }}>
                                                <i className="fas fa-bullseye fa-2x text-white"></i>
                                            </div>
                                        </div>
                                        <h3 className="mb-3" style={{
                                            color: 'var(--primary-red)',
                                            fontSize: '1.75rem',
                                            fontWeight: '700',
                                            background: 'linear-gradient(135deg, var(--primary-red), var(--light-red))',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}>
                                            Our Mission
                                        </h3>
                                        <div style={{
                                            width: '50px',
                                            height: '4px',
                                            background: 'linear-gradient(90deg, var(--primary-red), var(--light-red))',
                                            margin: '0 auto 20px',
                                            borderRadius: '2px'
                                        }}></div>
                                    </div>

                                    {/* Mission Content */}
                                    <div className="mb-4">
                                        <p className="text-dark-charcoal mb-4" style={{
                                            fontSize: '1.1rem',
                                            lineHeight: '1.6',
                                            fontWeight: '500',
                                            textAlign: 'center'
                                        }}>
                                            To deliver superior engineering solutions through innovation,
                                            technical expertise, and unwavering commitment to quality.
                                        </p>

                                        {/* Mission Goals with Icons */}
                                        <div className="row g-3">
                                            {[
                                                { icon: 'fa-rocket', title: 'Innovation-driven Solutions', desc: 'Implementing cutting-edge technologies' },
                                                { icon: 'fa-graduation-cap', title: 'Technical Excellence', desc: 'Leveraging deep expertise and best practices' },
                                                { icon: 'fa-medal', title: 'Quality Commitment', desc: 'Uncompromising standards in delivery' },
                                                { icon: 'fa-leaf', title: 'Sustainable Practices', desc: 'Eco-friendly solutions for tomorrow' }
                                            ].map((goal, idx) => (
                                                <div key={idx} className="col-12">
                                                    <div className="d-flex align-items-start p-4" style={{
                                                        background: 'rgba(199, 53, 63, 0.05)',
                                                        borderRadius: '12px',
                                                        borderLeft: '4px solid var(--primary-red)',
                                                        transition: 'all 0.3s ease',
                                                        backdropFilter: 'blur(5px)',
                                                        border: '1px solid rgba(199, 53, 63, 0.1)'
                                                    }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            background: 'var(--primary-red)',
                                                            borderRadius: '10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginRight: '15px',
                                                            flexShrink: 0,
                                                            boxShadow: '0 4px 12px rgba(199, 53, 63, 0.2)'
                                                        }}>
                                                            <i className={`fas ${goal.icon} text-white`}></i>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <h6 className="mb-1" style={{
                                                                color: 'var(--primary-red)',
                                                                fontWeight: '600'
                                                            }}>
                                                                {goal.title}
                                                            </h6>
                                                            <p className="mb-0" style={{
                                                                color: 'var(--cool-grey)',
                                                                fontSize: '0.9rem',
                                                                lineHeight: '1.5'
                                                            }}>
                                                                {goal.desc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Mission Footer - Red Theme */}
                                <div style={{
                                    background: 'linear-gradient(90deg, var(--primary-red), var(--light-red))',
                                    color: 'white',
                                    padding: '20px 30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '15px',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                    <i className="fas fa-quote-left"></i>
                                    <p className="mb-0">Exceeding expectations through engineering excellence</p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Vision Card - Glass-like with Blue Theme */}
                        <motion.div
                            className="col-lg-6"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div className="glass-card h-100" style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                background: 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.8)',
                                boxShadow: '0 8px 32px rgba(46, 47, 91, 0.1)',
                                position: 'relative'
                            }}>
                                {/* Blue gradient border */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, var(--primary-blue), var(--light-blue))'
                                }}></div>
                                
                                <div className="card-body p-4 p-md-5">
                                    {/* Enhanced Vision Icon with Glass Effect */}
                                    <div className="text-center mb-4">
                                        <div style={{
                                            width: '90px',
                                            height: '90px',
                                            background: 'linear-gradient(135deg, rgba(46, 47, 91, 0.2), rgba(74, 90, 181, 0.1))',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 20px',
                                            position: 'relative',
                                            backdropFilter: 'blur(5px)',
                                            border: '1px solid rgba(46, 47, 91, 0.3)',
                                            boxShadow: '0 8px 32px rgba(46, 47, 91, 0.15)'
                                        }}>
                                            <div style={{
                                                width: '70px',
                                                height: '70px',
                                                background: 'linear-gradient(135deg, var(--primary-blue), var(--light-blue))',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 15px rgba(46, 47, 91, 0.3)',
                                                animation: 'pulse 2s ease-in-out infinite'
                                            }}>
                                                <i className="fas fa-eye fa-2x text-white"></i>
                                            </div>
                                        </div>
                                        <h3 className="mb-3" style={{
                                            color: 'var(--primary-blue)',
                                            fontSize: '1.75rem',
                                            fontWeight: '700',
                                            background: 'linear-gradient(135deg, var(--primary-blue), var(--light-blue))',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}>
                                            Our Vision
                                        </h3>
                                        <div style={{
                                            width: '50px',
                                            height: '4px',
                                            background: 'linear-gradient(90deg, var(--primary-blue), var(--light-blue))',
                                            margin: '0 auto 20px',
                                            borderRadius: '2px'
                                        }}></div>
                                    </div>

                                    {/* Vision Content */}
                                    <div className="mb-4">
                                        <p className="text-dark-charcoal mb-4" style={{
                                            fontSize: '1.1rem',
                                            lineHeight: '1.6',
                                            fontWeight: '500',
                                            textAlign: 'center'
                                        }}>
                                            To be the most trusted and preferred engineering service provider
                                            in the GCC region, recognized for technical excellence and innovation.
                                        </p>

                                        {/* Vision Goals with Icons */}
                                        <div className="row g-3">
                                            {[
                                                { icon: 'fa-trophy', title: 'Industry Leadership', desc: 'Setting benchmarks in engineering excellence' },
                                                { icon: 'fa-chart-line', title: 'Sustainable Growth', desc: 'Building eco-friendly, sustainable communities' },
                                                { icon: 'fa-map-marked-alt', title: 'Regional Recognition', desc: 'Preferred partner across the GCC region' },
                                                { icon: 'fa-globe-asia', title: 'Global Standards', desc: 'Adhering to international quality standards' }
                                            ].map((goal, idx) => (
                                                <div key={idx} className="col-12">
                                                    <div className="d-flex align-items-start p-4" style={{
                                                        background: 'rgba(46, 47, 91, 0.05)',
                                                        borderRadius: '12px',
                                                        borderLeft: '4px solid var(--primary-blue)',
                                                        transition: 'all 0.3s ease',
                                                        backdropFilter: 'blur(5px)',
                                                        border: '1px solid rgba(46, 47, 91, 0.1)'
                                                    }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            background: 'var(--primary-blue)',
                                                            borderRadius: '10px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginRight: '15px',
                                                            flexShrink: 0,
                                                            boxShadow: '0 4px 12px rgba(46, 47, 91, 0.2)'
                                                        }}>
                                                            <i className={`fas ${goal.icon} text-white`}></i>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <h6 className="mb-1" style={{
                                                                color: 'var(--primary-blue)',
                                                                fontWeight: '600'
                                                            }}>
                                                                {goal.title}
                                                            </h6>
                                                            <p className="mb-0" style={{
                                                                color: 'var(--cool-grey)',
                                                                fontSize: '0.9rem',
                                                                lineHeight: '1.5'
                                                            }}>
                                                                {goal.desc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Vision Footer - Blue Theme (Same design as Mission) */}
                                <div style={{
                                    background: 'linear-gradient(90deg, var(--primary-blue), var(--light-blue))',
                                    color: 'white',
                                    padding: '20px 30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '15px',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                    <i className="fas fa-quote-left"></i>
                                    <p className="mb-0">Building the future of engineering in the Middle East</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title d-inline-block">Our Core Values</h2>
                        <p className="text-cool-grey mt-3 mb-0" style={{ display: 'block' }}>
                            Principles that guide everything we do
                        </p>
                    </motion.div>

                    <div className="row">
                        {[
                            { icon: 'fa-handshake', title: 'Integrity', desc: 'Honest, transparent dealings in all business relationships' },
                            { icon: 'fa-medal', title: 'Excellence', desc: 'Uncompromising commitment to quality and precision' },
                            { icon: 'fa-users', title: 'Teamwork', desc: 'Collaborative approach to achieve common goals' },
                            { icon: 'fa-lightbulb', title: 'Innovation', desc: 'Embracing new technologies and methodologies' },
                            { icon: 'fa-shield-alt', title: 'Safety', desc: 'Highest priority for people and property protection' },
                            { icon: 'fa-clock', title: 'Reliability', desc: 'Consistent, dependable service delivery' },
                        ].map((value, index) => (
                            <motion.div
                                key={index}
                                className="col-md-4 col-6 mb-4"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="text-center p-4">
                                    <div className="feature-icon mx-auto mb-3">
                                        <i className={`fas ${value.icon}`}></i>
                                    </div>
                                    <h5 className="mb-3">{value.title}</h5>
                                    <p className="text-cool-grey small mb-0">{value.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-5 bg-primary-blue">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-5 text-white"
                    >
                        <h2 className="section-title d-inline-block text-white">Leadership Team</h2>
                        <p className="text-off-white mt-3 mb-0" style={{ display: 'block' }}>
                            Meet the experts behind our success
                        </p>
                    </motion.div>

                    <div className="row">
                        {teamMembers.map((member, index) => (
                            <TeamCard key={member.id} member={member} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="py-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title d-inline-block">Certifications & Licenses</h2>
                        <p className="text-cool-grey mt-3 mb-0" style={{ display: 'block' }}>
                            Fully compliant with UAE regulations and standards
                        </p>
                    </motion.div>

                    <div className="row justify-content-center">
                        <motion.div
                            className="col-md-3 col-6 mb-4"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body text-center p-4">
                                    <i className="fas fa-file-certificate text-primary-red fa-3x mb-3"></i>
                                    <h5>Trade License</h5>
                                    <p className="small text-cool-grey mb-0">No. 880897</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="col-md-3 col-6 mb-4"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body text-center p-4">
                                    <i className="fas fa-id-card text-primary-red fa-3x mb-3"></i>
                                    <h5>TRN Number</h5>
                                    <p className="small text-cool-grey mb-0">104929219400003</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="col-md-3 col-6 mb-4"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body text-center p-4">
                                    <i className="fas fa-building text-primary-red fa-3x mb-3"></i>
                                    <h5>Registration</h5>
                                    <p className="small text-cool-grey mb-0">No. 2552101</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="col-md-3 col-6 mb-4"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                        >
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body text-center p-4">
                                    <i className="fas fa-shield-alt text-primary-red fa-3x mb-3"></i>
                                    <h5>ISO Certified</h5>
                                    <p className="small text-cool-grey mb-0">Quality Management</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-5 bg-off-white">
                <div className="container">
                    <div className="row">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                className="col-md-3 col-6 mb-4"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="stat-card">
                                    <div className="stat-number">{stat.value}</div>
                                    <h6 className="mb-0">{stat.label}</h6>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-5">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="bg-primary-blue rounded-3 p-5 text-center text-white"
                    >
                        <h3 className="mb-4">Partner With Us</h3>
                        <p className="mb-4 text-off-white">
                            Join hundreds of satisfied clients who trust LAMSAT for their engineering needs.
                            Let's build excellence together.
                        </p>
                        <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                            <a href="/contact" className="btn btn-primary-custom">
                                Get in Touch
                            </a>
                            <a href="/services" className="btn btn-outline-custom">
                                Our Services
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </>
    );
};

export default About;